// Hook for managing PWA install prompts on Android (beforeinstallprompt) and iOS (UA detection)

import { useState, useEffect } from 'react'

const DISMISSED_KEY = 'pwa_install_dismissed'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false)
  const [showIOSPrompt, setShowIOSPrompt] = useState(false)

  useEffect(() => {
    // Don't show if already permanently dismissed
    if (localStorage.getItem(DISMISSED_KEY)) return

    // Don't show if already running in standalone mode
    if (window.matchMedia('(display-mode: standalone)').matches) return

    // iOS detection — beforeinstallprompt never fires on iOS Safari
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)
    const isIOSStandalone = ('standalone' in navigator) && (navigator as Navigator & { standalone?: boolean }).standalone

    if (isIOS && !isIOSStandalone) {
      setShowIOSPrompt(true)
      return
    }

    // Android: listen for beforeinstallprompt event
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault() // suppress browser's default mini-infobar
      setDeferredPrompt(e)
      setShowAndroidPrompt(true)
    }

    window.addEventListener('beforeinstallprompt', handler as EventListener)
    return () => window.removeEventListener('beforeinstallprompt', handler as EventListener)
  }, [])

  const installApp = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setShowAndroidPrompt(false)
    }
    setDeferredPrompt(null)
  }

  const dismissPermanently = () => {
    localStorage.setItem(DISMISSED_KEY, '1')
    setShowAndroidPrompt(false)
    setShowIOSPrompt(false)
  }

  return { showAndroidPrompt, showIOSPrompt, installApp, dismissPermanently }
}
